import axios from 'axios';
import { ErrorHandler } from '../defaultApiErrorHandler';

export const RESTRICTED_FORM_NO_ACCESS_ERROR_TYPE = 'RESTRICTED_FORM_NO_ACCESS';
export const INVALID_ENCRYPTED_ID_ERROR_TYPE = 'INVALID_ENCRYPTED_ID';
export const SIMPLE_FORM_ARCHIVED_ERROR_TYPE = 'SIMPLE_FORM_ARCHIVED';

export class FormNotFoundError extends Error {
    constructor(message: string = 'Form not found or expired') {
        super(message);
        this.name = 'FormNotFoundError';
        // errorEvents.emit('form_not_found');
    }
}

export class FormValidationError extends Error {
    constructor(message: string = 'Form validation error') {
        super(message);
        this.name = 'FormValidationError';
        // errorEvents.emit('error_occured');
    }
}

export class RestrictedFormAccessError extends Error {
    constructor(
        message: string = 'You do not have permission to fill in this form',
    ) {
        super(message);
        this.name = 'RestrictedFormAccessError';
    }
}

export class InvalidFormLinkError extends Error {
    constructor(message: string = 'Invalid or corrupted form link') {
        super(message);
        this.name = 'InvalidFormLinkError';
    }
}

export class SimpleFormArchivedError extends Error {
    constructor(message: string = 'This form has been archived') {
        super(message);
        this.name = 'SimpleFormArchivedError';
    }
}

// Handler for issue POST: only errors that require navigating away (form archived
// mid-submit, user lost access, form deleted). No localStorage side-effects —
// the encryptedSimpleFormId is still valid; a rejected issue POST must not wipe it.
// FormNotFoundError is thrown for 404s but without the localStorage.removeItem
// that FormErrorHandler applies, because the form ID remains valid after a failed POST.
export class IssueErrorHandler implements ErrorHandler {
    handleError(error: unknown): never {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status;
            const message = error.response?.data?.error?.message;
            const errorType = error.response?.data?.error?.errorType;

            if (
                status === 403 &&
                errorType === RESTRICTED_FORM_NO_ACCESS_ERROR_TYPE
            ) {
                throw new RestrictedFormAccessError(message);
            }

            if (
                status === 410 &&
                errorType === SIMPLE_FORM_ARCHIVED_ERROR_TYPE
            ) {
                throw new SimpleFormArchivedError(message);
            }

            if (status === 404) {
                throw new FormNotFoundError(message);
            }
        }
        throw error;
    }
}

export class FormErrorHandler implements ErrorHandler {
    handleError(error: unknown): never {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status;
            const message = error.response?.data?.error?.message;
            const errorType = error.response?.data?.error?.errorType;

            if (
                status === 403 &&
                errorType === RESTRICTED_FORM_NO_ACCESS_ERROR_TYPE
            ) {
                throw new RestrictedFormAccessError(message);
            }

            if (
                status === 410 &&
                errorType === SIMPLE_FORM_ARCHIVED_ERROR_TYPE
            ) {
                throw new SimpleFormArchivedError(message);
            }

            if (
                status === 422 &&
                errorType === INVALID_ENCRYPTED_ID_ERROR_TYPE
            ) {
                localStorage.removeItem('encryptedSimpleFormId');
                throw new InvalidFormLinkError(message);
            }

            if (status === 404) {
                localStorage.removeItem('encryptedSimpleFormId');
                throw new FormNotFoundError(message);
            }

            if (status === 422) {
                localStorage.removeItem('encryptedSimpleFormId');
                throw new FormValidationError(message);
            }
        }
        throw error;
    }
}
